'use client'

import React, { useState, useRef, useEffect, useMemo } from 'react'
import { motion } from 'framer-motion'
import { useParams } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@/contexts/AuthContext'
import { supabase } from '@/integrations/supabase/client'
import { useToast } from '@/hooks/use-toast'
import RedditConnectionRequired from './RedditConnectionRequired'
import { 
  Target, 
  Search, 
  Plus, 
  ExternalLink, 
  Users, 
  CheckCircle, 
  AlertCircle, 
  RefreshCw, 
  Eye, 
  ThumbsUp, 
  Hash,
  Globe,
  Sparkles,
  Filter,
  TrendingUp,
  Shield,
  Trash2,
  Info,
  Tag
} from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog"
import { ScrollArea } from "@/components/ui/scroll-area"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { cn } from '@/lib/utils'

interface SubredditCandidate {
  name: string
  subscribers: number
  activeUsers?: number
  description: string
  difficulty: 'Easy' | 'Medium' | 'Hard'
  requirements: string[]
  isApproved: boolean
  validationStatus: 'valid' | 'insufficient_karma' | 'not_subscribed' | 'banned' | 'pending'
  iconUrl?: string
  category: string
  growthRate?: number
  difficulty_score?: number
  relevance_score?: number
}

interface CoreKeyword {
  id: string
  text: string
  relevance: number
}

function RedditSubredditResearchContent() {
  const { id: projectId } = useParams();
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  const [searchQuery, setSearchQuery] = useState('')
  const [isSearching, setIsSearching] = useState(false)
  const [selectedFilter, setSelectedFilter] = useState<string>('all')
  const [selectedCategory, setSelectedCategory] = useState<string>('all')
  const [activeTab, setActiveTab] = useState('discovery')

  // Flair selection state
  const [showFlairDialog, setShowFlairDialog] = useState(false);
  const [selectedSubredditForFlair, setSelectedSubredditForFlair] = useState<string>('');
  const [availableFlairs, setAvailableFlairs] = useState<string[]>([]);
  const [selectedFlair, setSelectedFlair] = useState<string>('');
  const [isFetchingFlairs, setIsFetchingFlairs] = useState(false);
  const [validationResultForFlair, setValidationResultForFlair] = useState<any>(null);
  const [customFlair, setCustomFlair] = useState('');

  // Fetch project keywords from website analysis
  const { data: keywordsData, isLoading: isLoadingKeywords } = useQuery({
    queryKey: ['project-keywords', projectId],
    queryFn: async () => {
      if (!projectId || !user?.id) return [];

      const { data: crawl, error } = await supabase
        .from('website_crawls')
        .select('id, analysis_results, status, created_at')
        .eq('project_id', projectId)
        .eq('status', 'completed')
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error || !crawl?.analysis_results) {
        return [];
      }

      try {
        let analysisData: any;
        if (typeof crawl.analysis_results === 'string') {
          analysisData = JSON.parse(crawl.analysis_results);
        } else {
          analysisData = crawl.analysis_results;
        }

        const keywords: CoreKeyword[] = [];
        
        // Extract only core keywords (limit to 6 for better UI)
        if (analysisData?.keywords?.core && Array.isArray(analysisData.keywords.core)) {
          analysisData.keywords.core.slice(0, 6).forEach((keyword: string, index: number) => {
            if (keyword && keyword.trim()) {
              keywords.push({
                id: `core-${index}`,
                text: keyword.trim(),
                relevance: 95 - (index * 3) // Start at 95% and decrease by 3% for each keyword
              });
            }
          });
        }

        return keywords;
      } catch (parseError) {
        console.error('Error parsing website analysis:', parseError);
        return [];
      }
    },
    enabled: !!projectId && !!user?.id,
    staleTime: 10 * 60 * 1000 // Cache for 10 minutes
  });

  // Get core keywords from project analysis, fallback to default suggestions if no data
  const coreKeywords: CoreKeyword[] = keywordsData && keywordsData.length > 0 
    ? keywordsData 
    : [
        { id: 'default-1', text: 'business', relevance: 95 },
        { id: 'default-2', text: 'marketing', relevance: 92 },
        { id: 'default-3', text: 'technology', relevance: 89 },
        { id: 'default-4', text: 'startup', relevance: 86 }
      ]

  // Fetch approved subreddits from backend
  const { data: approvedSubreddits = [], isLoading: isLoadingApproved } = useQuery({
    queryKey: ['approved-subreddits', projectId],
    queryFn: async () => {
      if (!projectId || !user?.id) return [];

      const { data, error } = await supabase
        .from('approved_subreddits')
        .select('*')
        .eq('project_id', projectId)
        .eq('user_id', user.id)
        .order('added_at', { ascending: false });

      if (error) {
        console.error('Error fetching approved subreddits:', error);
        return [];
      }

      return data?.map((sub: any) => ({
        name: sub.subreddit_name,
        subscribers: sub.subscribers || 0,
        description: sub.description || `${sub.subreddit_name} subreddit`,
        difficulty: (sub.required_karma || 0) > 500 ? 'Hard' : (sub.required_karma || 0) > 100 ? 'Medium' : 'Easy' as const,
        requirements: [
          ...(sub.required_karma ? [`${sub.required_karma}+ karma`] : []),
          ...(sub.validation_error_message ? [sub.validation_error_message] : [])
        ],
        isApproved: true,
        validationStatus: sub.validation_status === 'valid' ? 'valid' as const : 'insufficient_karma' as const,
        category: 'Business',
        difficulty_score: sub.required_karma || 0,
        default_flair: sub.default_flair
      })) || [];
    },
    enabled: !!projectId && !!user?.id,
    refetchInterval: 60000 // Refetch every minute
  })

  // Client-side NSFW filtering as a safety net for cached results
  const filterNSFWSubreddits = (subreddits: any[]) => {
    // Whitelist of legitimate business/tech subreddits that should never be filtered
    const legitSubreddits = [
      'awscertifications', 'indiehackers', 'nocodesaas', 'saassales', 'saasmarketing', 
      'micro_saas', 'saas_email_marketing', 'saasforsale', 'saasleads', 'business', 
      'entrepreneur', 'startups', 'marketing', 'sales', 'webdev', 'programming',
      'sideproject', 'bootstrapped', 'smallbusiness', 'freelance', 'remotework',
      'saas', 'analytics', 'growth', 'customerservice', 'productivity'
    ];

    const nsfwKeywords = [
      'nsfw', 'porn', 'adult', 'xxx', 'nude', 'naked', 'erotic',
      'sexual', 'slutty', 'slut', 'cock', 'dick', 'pussy', 'boobs', 
      'tits', 'nipple', 'orgasm', 'masturbat', 'horny',
      'hookup', 'r4r', 'personals', 'affair', 'cheating', 'escort',
      'fetish', 'kink', 'bdsm', 'femdom', 'sissy', 'cuckold', 'cuck',
      'chastity', 'bondage', 'submission', 
      'humiliation', 'degradation', 'gonewild',
      'milf', 'tribute', 'ratemybody',
      'amateur', 'homemade', 'leaked', 'onlyfans', 'webcam',
      'nsfl', 'thicc', 'pawg'
    ];

    return subreddits.filter(subreddit => {
      const name = subreddit.name.toLowerCase();
      const desc = (subreddit.description || '').toLowerCase();
      
      // First check if it's on the whitelist of legitimate subreddits
      if (legitSubreddits.includes(name)) {
        console.log(`✅ Whitelisted legitimate subreddit: ${name}`);
        return true; // Always allow whitelisted subreddits
      }
      
      // Check for explicit NSFW keywords with word boundaries to avoid false positives
      const hasNSFWKeyword = nsfwKeywords.some(keyword => {
        // Use word boundaries for better matching, but also check direct contains for shorter terms
        const wordBoundaryPattern = new RegExp(`\\b${keyword}\\b`, 'i');
        const directMatch = name.includes(keyword) || desc.includes(keyword);
        
        // For very short keywords (3 chars or less), be more strict
        if (keyword.length <= 3) {
          return wordBoundaryPattern.test(name) || wordBoundaryPattern.test(desc);
        } else {
          return directMatch;
        }
      });
      
      // Check for explicit NSFW patterns
      const hasNSFWPattern = (
        /(dirty|naughty|slutty|sexy)(confession|stories|secrets)/i.test(name) ||
        /(cuckold|sissy|femdom|bdsm)(verification|verified|stories)/i.test(name) ||
        /(rate|tribute|judge)(me|my|body|girls|women|guys)/i.test(name) ||
        /(verification|verified)(gone|wild|slutty|dirty)/i.test(name) ||
        /onlyfans|of[_\-]|fansonly/i.test(name) ||
        /^(r4r|personals|dirtyr4r|nsfwr4r)/i.test(name) ||
        /(gonewild|gw|slutty|dirty|naughty|horny)(pics|photos|stories|confessions)/i.test(name)
      );
      
      const isNSFW = hasNSFWKeyword || hasNSFWPattern;
      
      if (isNSFW) {
        console.log(`Client-side filtering out NSFW subreddit: ${name}`);
      }
      
      return !isNSFW;
    });
  };

  // Fetch search results from cache
  const { data: searchResults = [], isLoading: isLoadingSearch } = useQuery({
    queryKey: ['subreddit-search-results', projectId, searchQuery],
    queryFn: async () => {
      if (!projectId || !user?.id || !searchQuery.trim()) return [];

      // First try to get cached results
      const { data: cachedData, error: cacheError } = await supabase
        .from('subreddit_search_cache')
        .select('*')
        .eq('project_id', projectId)
        .eq('keyword_text', searchQuery.trim().toLowerCase())
        .order('fetched_at', { ascending: false })
        .limit(1);

      if (cacheError) {
        console.error('Error fetching search cache:', cacheError);
        return [];
      }

      if (cachedData && cachedData.length > 0) {
        const results = cachedData[0].results_json;
        if (Array.isArray(results)) {
          console.log(`Found ${results.length} cached search results for "${searchQuery}"`);
          const mappedResults = results.map((result: any) => ({
            name: result.name,
            subscribers: result.subscriber_count || 0,
            activeUsers: result.active_user_count,
            description: result.public_description || `${result.name} subreddit`,
            difficulty: (result.difficulty_score >= 4 ? 'Hard' : result.difficulty_score >= 3 ? 'Medium' : 'Easy') as 'Easy' | 'Medium' | 'Hard',
            requirements: [
              ...(result.min_karma_required ? [`${result.min_karma_required}+ karma`] : []),
              ...(result.requires_subscription ? ['Subscription required'] : [])
            ],
            isApproved: false,
            validationStatus: 'not_subscribed' as const,
            category: 'Business',
            difficulty_score: result.difficulty_score,
            relevance_score: result.relevance_score || 0
          }));
          
          // Apply client-side NSFW filtering as safety net
          const filteredResults = filterNSFWSubreddits(mappedResults);
          console.log(`Filtered ${mappedResults.length - filteredResults.length} NSFW subreddits from cached results`);
          return filteredResults;
        }
      }

      console.log(`No cached search results found for "${searchQuery}"`);
      return [];
    },
    enabled: !!projectId && !!user?.id && !!searchQuery.trim(),
    staleTime: 5 * 60 * 1000 // Consider data stale after 5 minutes
  })

  // Combined list of all subreddits (approved + search results)
  const subredditCandidates = [
    ...(approvedSubreddits || []).map((approved: any) => ({
      ...approved,
      name: approved.subreddit_name || approved.name,
      subscribers: approved.subscribers || 0,
      isApproved: true,
      validationStatus: approved.validation_status || 'valid'
    })),
    ...((searchResults || []).map(result => ({
      ...result,
      isApproved: (approvedSubreddits || []).some((approved: any) => 
        (approved.subreddit_name || approved.name) === result.name
      ),
      validationStatus: (approvedSubreddits || []).some((approved: any) => 
        (approved.subreddit_name || approved.name) === result.name
      ) ? 'valid' : 'not_subscribed'
    })))
  ]

  // Remove duplicates by name, keeping the version with most complete data
  const uniqueSubreddits = subredditCandidates.reduce((acc, current) => {
    const existing = acc.find(item => item.name === current.name);
    if (!existing) {
      acc.push(current);
    } else {
      // Merge the data, preferring approved status but keeping highest subscriber count
      const merged = {
        ...existing,
        ...current,
        isApproved: existing.isApproved || current.isApproved,
        subscribers: Math.max(existing.subscribers || 0, current.subscribers || 0),
        activeUsers: Math.max(existing.activeUsers || 0, current.activeUsers || 0),
        validationStatus: existing.isApproved ? existing.validationStatus : current.validationStatus,
        // Keep the better description if available
        description: (current.description && current.description !== `${current.name} subreddit`) 
          ? current.description 
          : existing.description
      };
      const index = acc.findIndex(item => item.name === current.name);
      acc[index] = merged;
    }
    return acc;
  }, [] as typeof subredditCandidates);

  const searchSubreddits = async (keyword?: string, forceRefresh = false) => {
    if (!projectId || !user?.id) {
      toast({
        title: "Error",
        description: "Project not found. Please try again.",
        variant: "destructive"
      });
      return;
    }

    const query = keyword || searchQuery.trim();
    if (!query) {
      toast({
        title: "Error",
        description: "Please enter a search keyword.",
        variant: "destructive"
      });
      return;
    }

    setIsSearching(true);
    
    try {
      // If force refresh, clear cache first
      if (forceRefresh) {
        await supabase
          .from('subreddit_search_cache')
          .delete()
          .eq('project_id', projectId)
          .eq('keyword_text', query.toLowerCase());
      }

      // Call the search edge function
      const { data, error } = await supabase.functions.invoke('reddit-search-subreddits', {
        body: {
          keyword: query,
          projectId: projectId,
          userId: user.id,
          forceRefresh: forceRefresh
        }
      });

      if (error) {
        console.error('Search error:', error);
        toast({
          title: "Search Failed",
          description: "Failed to search subreddits. Please try again.",
          variant: "destructive"
        });
      } else {
        const subredditCount = data?.data?.subreddits?.length || 0;
        toast({
          title: "Search Complete",
          description: `Found ${subredditCount} subreddits matching "${query}"${forceRefresh ? ' (fresh results)' : ''}`,
        });
        
        // Update the search query state to trigger refetch
        if (keyword) {
          setSearchQuery(query);
        }
        
        // Force refetch search results with a small delay to ensure cache is updated
        setTimeout(() => {
          queryClient.invalidateQueries({ queryKey: ['subreddit-search-results', projectId, query] });
        }, 1000);
      }
    } catch (error) {
      console.error('Search error:', error);
      toast({
        title: "Search Failed",
        description: "An unexpected error occurred. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSearching(false);
    }
  }

  const fetchAvailableFlairs = async (subredditName: string) => {
    if (!projectId || !user?.id) return [];

    setIsFetchingFlairs(true);
    try {
      console.log(`🔍 Starting flair fetch for r/${subredditName}...`);
      console.log(`📋 Request details:`, {
        subredditName,
        userId: user.id,
        projectId,
        action: 'fetch_flairs'
      });
      
      const { data, error } = await supabase.functions.invoke('reddit-publisher-enhanced', {
        body: {
          action: 'fetch_flairs',
          subredditName: subredditName,
          userId: user.id,
          projectId: projectId
        }
      });

      console.log(`📥 Flair fetch response for r/${subredditName}:`, { 
        data, 
        error,
        hasError: !!error,
        dataStructure: data ? Object.keys(data) : 'no data'
      });

      if (error) {
        console.error('❌ Error fetching flairs:', error);
        console.error('❌ Error details:', {
          message: error.message,
          details: error.details,
          hint: error.hint,
          code: error.code
        });
        // Remove flair fetch error toast - let the approval process handle errors
        return [];
      }

      const flairs = data?.data?.flairs || [];
      const method = data?.data?.method || 'unknown';
      
      console.log(`✅ Found ${flairs.length} flairs for r/${subredditName} via ${method}:`, flairs);
      console.log(`📊 Flair details:`, {
        method,
        flairsCount: flairs.length,
        flairsArray: flairs,
        rawResponse: data
      });
      
      // Remove "No flairs found" toast as it's not critical information
      if (flairs.length === 0) {
        console.log(`⚠️  No flairs available for r/${subredditName} - this might be normal for this subreddit`);
      }
      
      return flairs;
    } catch (error) {
      console.error('❌ Exception fetching flairs:', error);
      console.error('❌ Exception details:', {
        name: error.name,
        message: error.message,
        stack: error.stack
      });
      // Remove flair fetch error toast - let the approval process handle errors
      return [];
    } finally {
      console.log(`🏁 Flair fetch completed for r/${subredditName}`);
      setIsFetchingFlairs(false);
    }
  };

  const approveSubredditWithFlair = async (subredditName: string, selectedFlair?: string, validationResult?: any) => {
    if (!projectId || !user?.id) return;

    try {
      // Find the subreddit in our search results to get its metadata
      const subredditData = uniqueSubreddits.find(sub => sub.name === subredditName);
      
      const { error: insertError } = await supabase
        .from('approved_subreddits')
        .insert({
          project_id: projectId,
          user_id: user.id,
          subreddit_name: subredditName,
          validation_status: 'valid',
          auto_subscribe_attempted: false,
          allow_autoposting: true,
          default_flair: selectedFlair || null,
          available_flairs: availableFlairs.length > 0 ? availableFlairs : null,
          subscribers: subredditData?.subscribers || 0,
          description: subredditData?.description || `${subredditName} subreddit`,
          required_karma: subredditData?.difficulty_score || 0,
          added_at: new Date().toISOString(),
          last_validated_at: new Date().toISOString()
        });

      if (insertError) {
        console.error('Error adding subreddit to approved list:', insertError);
        toast({
          title: "Approval Failed",
          description: `Failed to add r/${subredditName} to approved list.`,
          variant: "destructive"
        });
      } else {
        // Create comprehensive success message
        let successMessage = `r/${subredditName} added to approved list`;
        
        if (selectedFlair) {
          successMessage += ` with flair "${selectedFlair}"`;
        }
        
        if (validationResult) {
          if (validationResult.can_post) {
            successMessage += '. ✅ Ready for posting';
          } else {
            successMessage += '. ⚠️ May have posting restrictions';
          }
        }
        
        successMessage += '!';
        
        toast({
          title: "Subreddit Approved",
          description: successMessage,
        });
        
        // Immediately refetch approved subreddits to update the UI
        queryClient.invalidateQueries({ queryKey: ['approved-subreddits', projectId] });
        
        // Reset flair dialog state
        setShowFlairDialog(false);
        setSelectedSubredditForFlair('');
        setAvailableFlairs([]);
        setSelectedFlair('');
        setValidationResultForFlair(null);
      }
    } catch (insertError) {
      console.error('Error adding subreddit:', insertError);
      toast({
        title: "Approval Failed",
        description: `Failed to add r/${subredditName} to approved list.`,
        variant: "destructive"
      });
    }
  };

  const handleApproveWithFlairSelection = async (subredditName: string, validationResult?: any) => {
    console.log(`🎯 Starting approval flow for r/${subredditName}...`);
    console.log(`📋 Validation result:`, validationResult);
    
    setSelectedSubredditForFlair(subredditName);
    setSelectedFlair('');
    setValidationResultForFlair(validationResult);
    
    console.log(`🔍 About to fetch flairs for r/${subredditName}...`);
    // Fetch available flairs
    const flairs = await fetchAvailableFlairs(subredditName);
    console.log(`📥 Received flairs:`, flairs);
    setAvailableFlairs(flairs);
    
    if (flairs.length > 0) {
      console.log(`✅ Showing flair dialog for r/${subredditName} with ${flairs.length} flairs`);
      // Show flair selection dialog
      setShowFlairDialog(true);
    } else {
      console.log(`⚠️  No flairs found, approving r/${subredditName} directly`);
      // No flairs available, approve directly
      await approveSubredditWithFlair(subredditName, undefined, validationResult);
    }
  };

  const validateSubreddit = async (subredditName: string) => {
    if (!projectId || !user?.id) return;

    try {
      const { data, error } = await supabase.functions.invoke('reddit-validate-subreddit-access', {
        body: {
          subredditName: subredditName,
          projectId: projectId,
          userId: user.id,
          autoSubscribe: false
        }
      });

      if (error) {
        console.error('Validation error:', error);
        toast({
          title: "Validation Failed",
          description: `Failed to validate r/${subredditName}. You can still approve it to try posting later.`,
          variant: "destructive"
        });
        
        // Even if validation fails, allow approval with flair selection
        await handleApproveWithFlairSelection(subredditName, data.data || data);
      } else {
        const result = data.data || data;
        
        // Create status message based on validation results
        let statusMessage = '';
        let variant: 'default' | 'destructive' = 'default';
        
        if (result.can_post) {
          statusMessage = `✅ r/${subredditName} is ready for posting!`;
          if (result.subscription_successful) {
            statusMessage += ' ✅ Auto-subscribed successfully.';
          } else if (result.subscription_attempted) {
            statusMessage += ' ⚠️ Auto-subscription attempted but not verified - please check Reddit manually.';
            variant = 'default';
          } else if (result.is_subscribed) {
            statusMessage += ' Already subscribed.';
          }
        } else {
          variant = 'destructive';
          statusMessage = `⚠️ r/${subredditName} may have posting restrictions`;
          
          if (result.issues && result.issues.length > 0) {
            statusMessage += `: ${result.issues.join(', ')}.`;
          } else {
            statusMessage += '.';
          }
        }
        
        // Simplified subscription guidance
        if (!result.is_subscribed && !result.subscription_attempted) {
          statusMessage += ' Consider subscribing on Reddit first.';
        }
        
        // Proceed with flair selection and approval
        await handleApproveWithFlairSelection(subredditName, result);
      }
    } catch (error) {
      console.error('Validation error:', error);
      toast({
        title: "Validation Failed",
        description: "An unexpected error occurred. You can still approve the subreddit to try posting later.",
        variant: "destructive"
      });
      
      // Allow approval even if validation fails
      await handleApproveWithFlairSelection(subredditName, null);
    }
  }

  const handleKeywordClick = (keyword: string) => {
    setSearchQuery(keyword)
    searchSubreddits(keyword)
  }

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'Easy': return 'bg-green-50 text-green-700 border-green-200'
      case 'Medium': return 'bg-amber-50 text-amber-700 border-amber-200'
      case 'Hard': return 'bg-red-50 text-red-700 border-red-200'
      default: return 'bg-zinc-50 text-zinc-700 border-zinc-200'
    }
  }

  const getValidationStatusColor = (status: SubredditCandidate['validationStatus']) => {
    switch (status) {
      case 'valid': return 'text-green-600'
      case 'insufficient_karma': return 'text-amber-600'
      case 'not_subscribed': return 'text-blue-600'
      case 'banned': return 'text-red-600'
      case 'pending': return 'text-zinc-600'
      default: return 'text-zinc-600'
    }
  }

  const getValidationStatusIcon = (status: SubredditCandidate['validationStatus']) => {
    switch (status) {
      case 'valid': return <CheckCircle className="h-4 w-4" />
      case 'insufficient_karma': return <AlertCircle className="h-4 w-4" />
      case 'not_subscribed': return <Users className="h-4 w-4" />
      case 'banned': return <Shield className="h-4 w-4" />
      case 'pending': return <RefreshCw className="h-4 w-4 animate-spin" />
      default: return <AlertCircle className="h-4 w-4" />
    }
  }

  const filteredSubreddits = (uniqueSubreddits || []).filter(subreddit => {
    if (selectedFilter === 'approved' && !subreddit.isApproved) return false
    if (selectedFilter === 'available' && subreddit.isApproved) return false
    if (selectedFilter === 'valid' && subreddit.validationStatus !== 'valid') return false
    if (selectedFilter === 'high_relevance' && (!subreddit.relevance_score || subreddit.relevance_score < 200)) return false
    if (selectedCategory !== 'all' && subreddit.category !== selectedCategory) return false
    return true
  }).sort((a, b) => {
    // Sort by relevance score for search results, approved subreddits at the end
    if (a.isApproved && !b.isApproved) return 1
    if (!a.isApproved && b.isApproved) return -1
    
    // For search results, sort by relevance score
    if (!a.isApproved && !b.isApproved) {
      const aRelevance = a.relevance_score || 0
      const bRelevance = b.relevance_score || 0
      if (aRelevance !== bRelevance) return bRelevance - aRelevance
    }
    
    // Fallback to subscriber count
    return (b.subscribers || 0) - (a.subscribers || 0)
  })

  const handleFinalApproval = async (selectedFlair: string) => {
    if (!projectId || !user?.id) return;

    try {
      await approveSubredditWithFlair(selectedSubredditForFlair, selectedFlair, validationResultForFlair);
    } catch (error) {
      console.error('Error approving subreddit:', error);
      toast({
        title: "Approval Failed",
        description: "Failed to approve the subreddit. Please try again later.",
        variant: "destructive"
      });
    }
  };

  return (
    <div className="container py-6 max-w-7xl bg-white dark:bg-zinc-900 min-h-screen">
      <div className="flex flex-col space-y-8">
        {/* Header */}
        <motion.div 
          className="flex items-center justify-between"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="flex items-center gap-4">
            <div>
              <h1 className="text-3xl font-bold tracking-tight bg-gradient-to-r from-blue-600 to-blue-600 bg-clip-text text-transparent">
                Subreddit Research
              </h1>
              <p className="text-zinc-500 dark:text-zinc-400 mt-1">
                Discover and manage subreddits for your AI agent
              </p>
            </div>
          </div>
        </motion.div>

        {/* Main Search Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          <Card className="border-0 shadow-lg bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700">
            <CardContent className="p-6">
              {/* Search Bar - Main Feature */}
              <div className="flex gap-3 mb-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-zinc-400 dark:text-zinc-500" />
                  <Input
                    placeholder="Search for subreddits by keyword or topic..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 h-12 text-base border-zinc-200 dark:border-zinc-700 focus:border-blue-500 focus:ring-blue-500 bg-white dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 placeholder:text-zinc-500 dark:placeholder:text-zinc-400"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        searchSubreddits(searchQuery)
                      }
                    }}
                  />
                </div>
                <Button 
                  onClick={() => searchSubreddits(searchQuery)}
                  disabled={isSearching}
                  className="h-12 px-8 bg-gradient-to-r from-blue-600 to-blue-600 hover:from-blue-700 hover:to-blue-700"
                >
                  {isSearching ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Searching...
                    </>
                  ) : (
                    <>
                      <Search className="h-4 w-4 mr-2" />
                      Search
                    </>
                  )}
                </Button>
                {searchQuery.trim() && searchResults.length > 0 && (
                  <Button 
                    onClick={() => searchSubreddits(searchQuery, true)}
                    disabled={isSearching}
                    variant="outline"
                    className="h-12 px-4 border-zinc-300 dark:border-zinc-600 hover:border-blue-400 hover:bg-blue-50 dark:hover:bg-zinc-700 bg-white dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300"
                    title="Force refresh - get fresh results from Reddit"
                  >
                    <RefreshCw className="h-4 w-4" />
                  </Button>
                )}
                {/* Debug Test Button - Remove in production */}
                  <Button 
                    onClick={async () => {
                    console.log(`🧪 DEBUG: Testing flair fetch for r/startups`);
                    const testFlairs = await fetchAvailableFlairs('startups');
                    console.log(`🧪 DEBUG: Test result:`, testFlairs);
                    toast({
                      title: "Flair Test Complete",
                      description: `Found ${testFlairs.length} flairs for r/startups. Check console for details.`,
                    });
                  }}
                  variant="outline"
                  className="h-12 px-4 border-yellow-400 text-yellow-700 hover:bg-yellow-50"
                  title="Test flair fetching on r/startups"
                >
                  🧪 Test
                </Button>
                
                {/* Playwright Direct Test Button */}
                <Button 
                  onClick={async () => {
                    if (!projectId || !user?.id) return;
                    
                    console.log(`🎭 DEBUG: Direct Playwright test for r/DigitalMarketing`);
                    
                    try {
                      const { data, error } = await supabase.functions.invoke('reddit-publisher-playwright', {
                        body: {
                          action: 'fetch_flairs',
                          subredditName: 'DigitalMarketing',
                          userId: user.id,
                          projectId: projectId
                        }
                      });

                      console.log(`🎭 DEBUG: Direct Playwright response:`, { data, error });
                      
                      if (error) {
                        console.error('🎭 DEBUG: Playwright error:', error);
                        toast({
                          title: "Playwright Test Failed",
                          description: `Error: ${error.message}`,
                          variant: "destructive"
                        });
                      } else {
                        const flairs = data?.data?.flairs || [];
                        console.log(`🎭 DEBUG: Direct Playwright flairs:`, flairs);
                        toast({
                          title: "Direct Playwright Test Complete",
                          description: `Found ${flairs.length} flairs directly via Playwright. Check console for details.`,
                        });
                      }
                    } catch (error) {
                      console.error('🎭 DEBUG: Playwright test exception:', error);
                      toast({
                        title: "Playwright Test Error",
                        description: `Exception: ${error.message}`,
                        variant: "destructive"
                        });
                      }
                    }}
                    variant="outline"
                  className="h-12 px-4 border-blue-400 text-blue-700 hover:bg-blue-50"
                  title="Test direct Playwright function call"
                  >
                  🎭 Direct Test
                  </Button>
              </div>

              {/* Core Keywords Pills - Smaller and directly under search */}
              <div className="space-y-2">
                <Label className="text-xs font-medium text-zinc-600 dark:text-zinc-400 flex items-center gap-1">
                  <Sparkles className="h-3 w-3 text-blue-500 dark:text-blue-400" />
                  Suggested keywords
                  {keywordsData && keywordsData.length > 0 && (
                    <Badge variant="secondary" className="text-[10px] bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 px-1.5 py-0.5 ml-1">
                      From project analysis
                    </Badge>
                  )}
                  {isLoadingKeywords && (
                    <RefreshCw className="h-3 w-3 text-zinc-400 animate-spin ml-1" />
                  )}
                </Label>
                <div className="flex flex-wrap gap-1.5">
                  {isLoadingKeywords ? (
                    // Loading skeleton for keywords
                    <>
                      {[1, 2, 3, 4, 5, 6].map((i) => (
                        <div key={i} className="h-6 w-16 bg-zinc-200 dark:bg-zinc-700 rounded-full animate-pulse"></div>
                      ))}
                    </>
                  ) : (
                    <>
                      {coreKeywords.map((keyword) => (
                        <motion.button
                          key={keyword.id}
                          onClick={() => handleKeywordClick(keyword.text)}
                          className="inline-flex items-center gap-1 px-2 py-1 bg-gradient-to-r from-blue-50 to-blue-50 dark:from-blue-950/30 dark:to-blue-950/30 border border-blue-200 dark:border-blue-800/40 rounded-full text-xs font-medium text-blue-700 dark:text-blue-400 hover:from-blue-100 hover:to-blue-100 dark:hover:from-blue-900/40 dark:hover:to-blue-900/40 hover:border-blue-300 dark:hover:border-blue-700 transition-all duration-200"
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                        >
                          <Hash className="h-2 w-2" />
                          {keyword.text}
                          <Badge variant="secondary" className="text-[9px] bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 px-1 py-0">
                            {keyword.relevance}%
                          </Badge>
                        </motion.button>
                      ))}
                      {!keywordsData || keywordsData.length === 0 ? (
                        <div className="text-xs text-zinc-500 dark:text-zinc-400 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800/40 rounded-lg px-3 py-2 flex items-center gap-2">
                          <AlertCircle className="h-3 w-3 text-amber-600 dark:text-amber-400" />
                          <span>
                            For better keyword suggestions, analyze your website in the{' '}
                            <a href="/research" className="text-amber-700 dark:text-amber-400 hover:text-amber-800 dark:hover:text-amber-300 font-medium underline">
                              Research section
                            </a>
                          </span>
                        </div>
                      ) : null}
                    </>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Tabs for Discovery vs Approved */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <TabsList className="grid w-fit grid-cols-2 bg-zinc-100 dark:bg-zinc-800 p-1 rounded-xl">
              <TabsTrigger 
                value="discovery" 
                className="data-[state=active]:bg-white dark:data-[state=active]:bg-zinc-700 data-[state=active]:shadow-sm rounded-lg text-zinc-700 dark:text-zinc-300 data-[state=active]:text-zinc-900 dark:data-[state=active]:text-zinc-100"
              >
                <Globe className="h-4 w-4 mr-2" />
                Discovery
              </TabsTrigger>
              <TabsTrigger 
                value="approved" 
                className="data-[state=active]:bg-white dark:data-[state=active]:bg-zinc-700 data-[state=active]:shadow-sm rounded-lg text-zinc-700 dark:text-zinc-300 data-[state=active]:text-zinc-900 dark:data-[state=active]:text-zinc-100"
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                Approved ({approvedSubreddits.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="discovery" className="space-y-4">
              {/* Search Insights Summary */}
              {searchQuery && searchResults.length > 0 && (
                <Card className="border-blue-200 dark:border-blue-800/40 bg-gradient-to-r from-blue-50 to-blue-50 dark:from-blue-950/30 dark:to-blue-950/30">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-semibold text-blue-900 dark:text-blue-200 flex items-center gap-2">
                          <Sparkles className="h-4 w-4" />
                          Search Insights for "{searchQuery}"
                        </h4>
                        <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                          Found {searchResults.length} subreddits with enhanced relevance scoring
                        </p>
                      </div>
                      <div className="flex gap-2">
                        {searchResults.filter(s => s.relevance_score && s.relevance_score > 300).length > 0 && (
                          <Badge className="bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 border-green-300 dark:border-green-800/40">
                            {searchResults.filter(s => s.relevance_score && s.relevance_score > 300).length} high relevance
                          </Badge>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Discovery Content */}
              <Card className="border-0 shadow-lg bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700">
                <CardHeader className="bg-gradient-to-r from-zinc-50 to-white dark:from-zinc-800 dark:to-zinc-800 border-b border-zinc-200 dark:border-zinc-700">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">
                        Subreddit Discovery
                      </CardTitle>
                      <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-1">
                        Find new communities to expand your reach
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                        <SelectTrigger className="w-40 bg-white dark:bg-zinc-800 border-zinc-200 dark:border-zinc-700 text-zinc-900 dark:text-zinc-100">
                          <SelectValue placeholder="Category" />
                        </SelectTrigger>
                        <SelectContent className="bg-white dark:bg-zinc-800 border-zinc-200 dark:border-zinc-700">
                          <SelectItem value="all" className="text-zinc-900 dark:text-zinc-100">All Categories</SelectItem>
                          <SelectItem value="Business" className="text-zinc-900 dark:text-zinc-100">Business</SelectItem>
                          <SelectItem value="Technology" className="text-zinc-900 dark:text-zinc-100">Technology</SelectItem>
                          <SelectItem value="Marketing" className="text-zinc-900 dark:text-zinc-100">Marketing</SelectItem>
                        </SelectContent>
                      </Select>
                      <Select value={selectedFilter} onValueChange={setSelectedFilter}>
                        <SelectTrigger className="w-40 bg-white dark:bg-zinc-800 border-zinc-200 dark:border-zinc-700 text-zinc-900 dark:text-zinc-100">
                          <SelectValue placeholder="Filter" />
                        </SelectTrigger>
                        <SelectContent className="bg-white dark:bg-zinc-800 border-zinc-200 dark:border-zinc-700">
                          <SelectItem value="all" className="text-zinc-900 dark:text-zinc-100">All Subreddits</SelectItem>
                          <SelectItem value="available" className="text-zinc-900 dark:text-zinc-100">Available</SelectItem>
                          <SelectItem value="valid" className="text-zinc-900 dark:text-zinc-100">Valid Only</SelectItem>
                          <SelectItem value="high_relevance" className="text-zinc-900 dark:text-zinc-100">High Relevance</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredSubreddits.filter(s => !s.isApproved).length > 0 ? (
                      filteredSubreddits.filter(s => !s.isApproved).map((subreddit, index) => (
                        <motion.div
                          key={subreddit.name}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.3, delay: index * 0.1 }}
                        >
                          {/* Subreddit Card Content */}
                          <Card className="h-full border border-zinc-200 dark:border-zinc-700 hover:shadow-lg transition-all duration-200 hover:border-blue-300 dark:hover:border-blue-600 bg-white dark:bg-zinc-800">
                            <CardContent className="p-6">
                              <div className="flex items-start justify-between mb-4">
                                <div className="flex items-center gap-3">
                                  <div className="w-10 h-10 bg-gradient-to-r from-blue-600 to-blue-600 rounded-lg flex items-center justify-center text-white font-bold">
                                    r/
                                  </div>
                                  <div>
                                    <h3 className="font-semibold text-zinc-900 dark:text-zinc-100 flex items-center gap-2">
                                      r/{subreddit.name}
                                      <a 
                                        href={`https://reddit.com/r/${subreddit.name}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-zinc-400 dark:text-zinc-500 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                                      >
                                        <ExternalLink className="h-4 w-4" />
                                      </a>
                                    </h3>
                                    <div className="flex items-center gap-2 mt-1">
                                      <Badge 
                                        variant="outline" 
                                        className={`text-xs ${getDifficultyColor(subreddit.difficulty)} dark:border-zinc-600`}
                                      >
                                        {subreddit.difficulty}
                                      </Badge>
                                      {subreddit.relevance_score && subreddit.relevance_score > 0 && (
                                        <Badge 
                                          variant="secondary" 
                                          className="text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 border-blue-200 dark:border-blue-800/40"
                                        >
                                          {subreddit.relevance_score} relevance
                                        </Badge>
                                      )}
                                      <div className={`flex items-center gap-1 text-xs ${getValidationStatusColor(subreddit.validationStatus)}`}>
                                        {getValidationStatusIcon(subreddit.validationStatus)}
                                        <span className="capitalize">{subreddit.validationStatus?.replace('_', ' ') || 'Unknown'}</span>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>

                              <p className="text-sm text-zinc-600 dark:text-zinc-300 mb-4 line-clamp-2">
                                {subreddit.description}
                              </p>

                              <div className="space-y-3 mb-4">
                                <div className="flex items-center justify-between text-sm">
                                  <div className="flex items-center gap-1 text-zinc-600 dark:text-zinc-400" key={`members-${subreddit.name}`}>
                                    <Users className="h-4 w-4" />
                                    <span>
                                      {subreddit.subscribers > 0 
                                        ? `${(subreddit.subscribers / 1000).toFixed(0)}k members`
                                        : 'Unknown members'
                                      }
                                    </span>
                                  </div>
                                </div>

                                {/* Display selected flair if available */}
                                {(subreddit as any).default_flair && (
                                  <div className="flex items-center gap-1 text-sm text-blue-600 dark:text-blue-400">
                                    <Tag className="h-4 w-4" />
                                    <span>Default flair: "{(subreddit as any).default_flair}"</span>
                                  </div>
                                )}

                                {/* Subscription reminder */}
                                <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800/40 rounded-lg p-3 mt-3">
                                  <div className="flex items-start gap-2">
                                    <div className="text-amber-600 dark:text-amber-400 mt-0.5">
                                      ⚠️
                                    </div>
                                    <div className="text-xs">
                                      <p className="font-medium text-amber-800 dark:text-amber-200 mb-1">Manual Subscription Required</p>
                                      <p className="text-amber-700 dark:text-amber-300">
                                        <a 
                                          href={`https://reddit.com/r/${subreddit.name}`}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="underline hover:no-underline font-medium"
                                        >
                                          Subscribe to r/{subreddit.name}
                                        </a>
                                        {' '}on Reddit for optimal posting success.
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              </div>

                              <Button 
                                className={`w-full ${
                                  subreddit.isApproved 
                                    ? 'bg-green-600 hover:bg-green-700 text-white cursor-default' 
                                    : 'bg-gradient-to-r from-blue-600 to-blue-600 hover:from-blue-700 hover:to-blue-700'
                                }`}
                                onClick={() => subreddit.isApproved ? null : validateSubreddit(subreddit.name)}
                                disabled={subreddit.isApproved}
                              >
                                {subreddit.isApproved ? (
                                  <>
                                    <CheckCircle className="h-4 w-4 mr-2" />
                                    Approved
                                  </>
                                ) : (
                                  <>
                                    <Plus className="h-4 w-4 mr-2" />
                                    Validate & Approve
                                  </>
                                )}
                              </Button>
                            </CardContent>
                          </Card>
                        </motion.div>
                      ))
                    ) : (
                      <div className="col-span-full text-center py-12">
                        {searchQuery ? (
                          <>
                            <Search className="h-12 w-12 text-zinc-300 dark:text-zinc-600 mx-auto mb-4" />
                            <h3 className="text-lg font-semibold text-zinc-600 dark:text-zinc-400 mb-2">
                              {isLoadingSearch ? 'Searching...' : 'No Results Found'}
                            </h3>
                            <p className="text-zinc-500 dark:text-zinc-400 mb-4">
                              {isLoadingSearch 
                                ? `Searching for subreddits matching "${searchQuery}"...`
                                : `No subreddits found matching "${searchQuery}". Try different keywords or check the search results above.`
                              }
                            </p>
                            {!isLoadingSearch && (
                              <Button 
                                variant="outline"
                                onClick={() => {
                                  setSearchQuery('');
                                  queryClient.invalidateQueries({ queryKey: ['subreddit-search-results'] });
                                }}
                                className="bg-white dark:bg-zinc-800 border-zinc-300 dark:border-zinc-600 text-zinc-700 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-700"
                              >
                                Clear Search
                              </Button>
                            )}
                          </>
                        ) : (
                          <>
                            <Target className="h-12 w-12 text-zinc-300 dark:text-zinc-600 mx-auto mb-4" />
                            <h3 className="text-lg font-semibold text-zinc-600 dark:text-zinc-400 mb-2">Discover Subreddits</h3>
                            <p className="text-zinc-500 dark:text-zinc-400 mb-4">
                              Search for subreddits using keywords or click on the suggested keywords above to get started.
                            </p>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="approved" className="space-y-4">
              {/* Approved Subreddits Content */}
              <Card className="border-0 shadow-lg bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700">
                <CardHeader className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-950/30 dark:to-emerald-950/30 border-b border-green-200 dark:border-green-800/40">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-lg font-semibold text-zinc-900 dark:text-zinc-100 flex items-center gap-2">
                        <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
                        Approved Subreddits
                      </CardTitle>
                      <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-1">
                        Communities approved for your AI agent to post in
                      </p>
                    </div>
                    <Badge variant="secondary" className="bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400">
                      {(approvedSubreddits || []).length} approved
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="p-6">
                  {approvedSubreddits.length > 0 ? (
                    <>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
                        {approvedSubreddits.map((subreddit, index) => (
                          <motion.div
                            key={subreddit.name}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.3, delay: index * 0.1 }}
                          >
                            <Card className="h-full border border-green-200 dark:border-green-800/40 bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-950/20 dark:to-emerald-950/20 hover:shadow-lg transition-all duration-200">
                              <CardContent className="p-6">
                                <div className="flex items-start justify-between mb-4">
                                  <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-gradient-to-r from-green-600 to-emerald-600 rounded-lg flex items-center justify-center text-white font-bold">
                                      r/
                                    </div>
                                    <div>
                                      <h3 className="font-semibold text-zinc-900 dark:text-zinc-100 flex items-center gap-2">
                                        r/{subreddit.name}
                                        <a 
                                          href={`https://reddit.com/r/${subreddit.name}`}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="text-zinc-400 dark:text-zinc-500 hover:text-green-600 dark:hover:text-green-400 transition-colors"
                                        >
                                          <ExternalLink className="h-4 w-4" />
                                        </a>
                                      </h3>
                                      <div className="flex items-center gap-2 mt-1">
                                        <Badge variant="outline" className="text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 border-green-300 dark:border-green-800/40">
                                          Approved
                                        </Badge>
                                        <div className={`flex items-center gap-1 text-xs ${getValidationStatusColor(subreddit.validationStatus)}`}>
                                          {getValidationStatusIcon(subreddit.validationStatus)}
                                          <span className="capitalize">{subreddit.validationStatus?.replace('_', ' ') || 'Unknown'}</span>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>

                                <p className="text-sm text-zinc-600 dark:text-zinc-300 mb-4 line-clamp-2">
                                  {subreddit.description}
                                </p>

                                <div className="space-y-3 mb-4">
                                  <div className="flex items-center justify-between text-sm">
                                    <div className="flex items-center gap-1 text-zinc-600 dark:text-zinc-400" key={`members-${subreddit.name}`}>
                                      <Users className="h-4 w-4" />
                                      <span>
                                        {subreddit.subscribers > 0 
                                          ? `${(subreddit.subscribers / 1000).toFixed(0)}k members`
                                          : 'Unknown members'
                                        }
                                      </span>
                                    </div>
                                  </div>

                                  {/* Display selected flair if available */}
                                  {(subreddit as any).default_flair && (
                                    <div className="flex items-center gap-1 text-sm text-blue-600 dark:text-blue-400">
                                      <Tag className="h-4 w-4" />
                                      <span>Default flair: "{(subreddit as any).default_flair}"</span>
                                    </div>
                                  )}

                                  {/* Subscription reminder */}
                                  <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800/40 rounded-lg p-3 mt-3">
                                    <div className="flex items-start gap-2">
                                      <div className="text-amber-600 dark:text-amber-400 mt-0.5">
                                        ⚠️
                                      </div>
                                      <div className="text-xs">
                                        <p className="font-medium text-amber-800 dark:text-amber-200 mb-1">Manual Subscription Required</p>
                                        <p className="text-amber-700 dark:text-amber-300">
                                          <a 
                                            href={`https://reddit.com/r/${subreddit.name}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="underline hover:no-underline font-medium"
                                          >
                                            Subscribe to r/{subreddit.name}
                                          </a>
                                          {' '}on Reddit for optimal posting success.
                                        </p>
                                      </div>
                                    </div>
                                  </div>
                                </div>

                                <div className="flex gap-2">
                                  <Button 
                                    variant="outline" 
                                    size="sm"
                                    className="flex-1 border-green-300 dark:border-green-700 text-green-700 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-950/30 bg-white dark:bg-zinc-800"
                                    onClick={() => validateSubreddit(subreddit.name)}
                                  >
                                    <RefreshCw className="h-4 w-4 mr-2" />
                                    Re-validate
                                  </Button>
                                  <Button 
                                    variant="outline" 
                                    size="sm"
                                    className="flex-1 border-red-300 dark:border-red-700 text-red-700 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-950/30 bg-white dark:bg-zinc-800"
                                    onClick={async () => {
                                      try {
                                        const { data: { user } } = await supabase.auth.getUser()
                                        if (!user || !projectId) return

                                        const { error } = await supabase
                                          .from('approved_subreddits')
                                          .delete()
                                          .eq('project_id', projectId)
                                          .eq('user_id', user.id)
                                          .eq('subreddit_name', subreddit.name)

                                        if (error) {
                                          console.error('Error removing subreddit:', error)
                                          toast({
                                            title: "Error",
                                            description: "Failed to remove subreddit. Please try again.",
                                            variant: "destructive"
                                          })
                                        } else {
                                          toast({
                                            title: "Subreddit Removed",
                                            description: `r/${subreddit.name} has been removed from your approved list.`
                                          })
                                          queryClient.invalidateQueries({ queryKey: ['approved-subreddits', projectId] })
                                        }
                                      } catch (error) {
                                        console.error('Error removing subreddit:', error)
                                        toast({
                                          title: "Error",
                                          description: "Failed to remove subreddit. Please try again.",
                                          variant: "destructive"
                                        })
                                      }
                                    }}
                                  >
                                    <Trash2 className="h-4 w-4 mr-2" />
                                    Remove
                                  </Button>
                                </div>
                              </CardContent>
                            </Card>
                          </motion.div>
                        ))}
                      </div>
                      
                      {/* Subscription Verification Tip - Moved below the cards */}
                      <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800/40 rounded-lg p-4">
                        <div className="flex items-start gap-3">
                          <div className="text-blue-600 dark:text-blue-400 mt-0.5">
                            💡
                          </div>
                          <div className="text-sm">
                            <p className="font-medium text-blue-900 dark:text-blue-200 mb-1">Important: Manual Subscription Required</p>
                            <p className="text-blue-700 dark:text-blue-300">
                              Each approved subreddit requires manual subscription for posting. Visit{' '}
                              <a 
                                href="https://reddit.com/subreddits/mine" 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="underline hover:no-underline font-medium"
                              >
                                reddit.com/subreddits/mine
                              </a>
                              {' '}to verify your subscriptions, or click the subscription links on each card above.
                            </p>
                          </div>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-12">
                      <CheckCircle className="h-12 w-12 text-zinc-300 dark:text-zinc-600 mx-auto mb-4" />
                      <h3 className="text-lg font-semibold text-zinc-600 dark:text-zinc-400 mb-2">No Approved Subreddits</h3>
                      <p className="text-zinc-500 dark:text-zinc-400 mb-4">
                        You have not approved any subreddits for your AI agent to post in.
                      </p>
                      <Button 
                        variant="outline"
                        onClick={() => setActiveTab('discovery')}
                        className="bg-white dark:bg-zinc-800 border-zinc-300 dark:border-zinc-600 text-zinc-700 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-700"
                      >
                        Discover Subreddits
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </motion.div>
      </div>

      {/* Flair Selection Dialog - ENHANCED WITH MANUAL ENTRY */}
      <Dialog open={showFlairDialog} onOpenChange={setShowFlairDialog}>
        <DialogContent className="sm:max-w-lg bg-white dark:bg-zinc-800 border-zinc-200 dark:border-zinc-700">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">
              Select Post Flair for r/{selectedSubredditForFlair}
            </DialogTitle>
            <DialogDescription className="text-sm text-zinc-600 dark:text-zinc-400">
              Choose a flair for your post or enter a custom one if the subreddit allows it.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {isFetchingFlairs ? (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="h-6 w-6 animate-spin text-blue-600 dark:text-blue-400" />
                <span className="ml-2 text-sm text-zinc-600 dark:text-zinc-400">Fetching available flairs...</span>
              </div>
            ) : availableFlairs.length > 0 ? (
              <div className="space-y-3">
                <Label className="text-sm font-medium text-zinc-700 dark:text-zinc-300">
                  Available Flairs
                    </Label>
                <div className="grid gap-2">
                  <div className="flex items-center space-x-2">
                    <input
                      type="radio"
                      id="no-flair"
                      name="flair"
                      value=""
                      checked={selectedFlair === ''}
                      onChange={(e) => setSelectedFlair(e.target.value)}
                      className="w-4 h-4 text-blue-600 border-zinc-300 focus:ring-blue-500"
                    />
                    <label 
                      htmlFor="no-flair" 
                      className="text-sm text-zinc-600 dark:text-zinc-400 cursor-pointer"
                    >
                      No flair
                    </label>
                  </div>
                  {availableFlairs.map((flair, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <input
                        type="radio"
                        id={`flair-${index}`}
                        name="flair"
                        value={flair}
                        checked={selectedFlair === flair}
                        onChange={(e) => setSelectedFlair(e.target.value)}
                        className="w-4 h-4 text-blue-600 border-zinc-300 focus:ring-blue-500"
                      />
                      <label 
                        htmlFor={`flair-${index}`} 
                        className="text-sm text-zinc-700 dark:text-zinc-300 cursor-pointer px-2 py-1 bg-zinc-100 dark:bg-zinc-700 rounded text-xs font-medium"
                      >
                          {flair}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-center py-6">
                <Info className="h-8 w-8 text-zinc-400 mx-auto mb-2" />
                <p className="text-sm text-zinc-600 dark:text-zinc-400 mb-4">
                  No flairs found automatically. This might be normal for this subreddit.
                </p>
              </div>
            )}

            {/* Manual Flair Entry Section */}
            <div className="mt-6 p-4 bg-amber-50 dark:bg-amber-950/20 rounded-lg border border-amber-200 dark:border-amber-800">
              <div className="flex items-start gap-3 mb-3">
                <AlertCircle className="h-5 w-5 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className="text-sm font-semibold text-amber-800 dark:text-amber-200 mb-1">
                    Manual Flair Entry - Safety Warning
                  </h4>
                  <p className="text-xs text-amber-700 dark:text-amber-300 leading-relaxed">
                    Some subreddits use <strong>"honeypot flairs"</strong> (like "ban me", "bot", "spam") to detect and ban automated systems. 
                    Only enter flairs you've manually verified from the subreddit's official list.
                  </p>
          </div>
              </div>
              <div className="flex gap-3">
                <Input
                  placeholder="Enter exact flair name (e.g., Discussion, News, Question)"
                  value={customFlair}
                  onChange={(e) => {
                    const value = e.target.value;
                    setCustomFlair(value);
                    
                    // Real-time safety check
                    const dangerousFlairs = ['ban me', 'ban-me', 'banme', 'bot', 'spam', 'delete', 'remove', 'violation', 'fake'];
                    const isDangerous = dangerousFlairs.some(dangerous => 
                      value.toLowerCase().includes(dangerous.toLowerCase())
                    );
                    
                    if (isDangerous) {
                      toast({
                        title: "⚠️ Dangerous Flair Detected",
                        description: `"${value}" appears to be a honeypot flair that could result in a ban. Please verify this is a legitimate flair.`,
                        variant: "destructive"
                      });
                    }
                  }}
                  className="flex-1 h-10 border-amber-200 dark:border-amber-700 focus:border-amber-400 focus:ring-amber-400 bg-white dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100"
                />
                <Button
                  onClick={() => {
                    if (customFlair.trim()) {
                      setSelectedFlair(customFlair.trim());
                      setCustomFlair('');
                      toast({
                        title: "Manual Flair Set",
                        description: `Using custom flair: "${customFlair.trim()}"`,
                      });
                    }
                  }}
                  size="sm"
                  className="px-4 bg-amber-600 hover:bg-amber-700 text-white"
                  disabled={!customFlair.trim()}
                >
                  Use Flair
                </Button>
              </div>
              <div className="mt-2 text-xs text-amber-600 dark:text-amber-400">
                <strong>Safe flairs to try:</strong> Discussion, News, Question, Advice, Resource, Tool, Update, Launch
              </div>
            </div>
          </div>
          
          <div className="flex justify-between pt-4">
            <Button
              variant="outline"
              onClick={() => setShowFlairDialog(false)}
              className="text-zinc-600 dark:text-zinc-400"
            >
              Cancel
            </Button>
            <Button
              onClick={() => handleFinalApproval(selectedFlair || customFlair)}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              Approve & Add to Queue
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default function RedditSubredditResearch() {
  return (
    <RedditConnectionRequired
      feature="Subreddit Research"
      description="Discover and validate subreddits where your AI agent can post content. Find the perfect communities for your target audience and ensure optimal engagement."
    >
      <RedditSubredditResearchContent />
    </RedditConnectionRequired>
  )
}
